import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CalendarDays, Check, X } from "lucide-react";

import { ApiError, api } from "../api/client";
import { Button, cx } from "./ui";
import { fromLocalInput, toLocalInput } from "../lib/datetime";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import { formatDateTime } from "../lib/format";
import type { Lead, LeadBooking } from "../api/types";

/**
 * Mark a lead won, and set the time properly on the way.
 *
 * The order matters and is the point of this panel: the time is saved first,
 * then the lead is converted. Winning a lead and only then discovering the time
 * was wrong leaves a customer with a booking nobody agreed.
 *
 * "They agreed this time" is separate again -- and stays separate. Agreeing a
 * date is not the same as being a customer, which is the collision that made the
 * Confirmed tile read zero while five rows wore a green CONFIRMED badge.
 *
 * The third step is the booking. Winning an enquiry with an agreed time holds
 * the slot (FR-6.5); this panel reports the outcome -- held, or not held and
 * why -- and hands the calendar the moment it could not. The win is not undone
 * by a full diary; the lead is won either way, and somebody picks a slot.
 */
export function WinLeadPanel({
  lead,
  onClose,
}: {
  lead: Lead;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const timeZone = useAccountTimeZone();

  const [when, setWhen] = useState(() => toLocalInput(lead.requested_start_at, timeZone));
  const [agreed, setAgreed] = useState(Boolean(lead.confirmed_at));
  const [done, setDone] = useState(false);

  const win = useMutation({
    mutationFn: async () => {
      const requested_start_at = fromLocalInput(when, timeZone);

      // Step one: the time. A failure here must not convert anything.
      await api.updateLead(lead.id, {
        requested_start_at,
        // `null` clears an agreement, which is right: re-agreed time, new answer.
        confirmed_at: agreed ? new Date().toISOString() : null,
      } as never);

      // Step two: the conversion -- which holds the agreed slot.
      return api.convertLead(lead.id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-summary"] });
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      void queryClient.invalidateQueries({ queryKey: ["lead", lead.id] });
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      setDone(true);
    },
  });

  // Step three: the booking's fate, reported once the win landed.
  const outcome = useQuery({
    queryKey: ["lead-booking", lead.id],
    queryFn: ({ signal }) => api.leadBooking(lead.id, signal),
    enabled: done,
  });

  const error = win.error instanceof ApiError ? win.error : null;

  if (done) {
    return (
      <div className="w-[24rem] max-w-full rounded-lg bg-emerald-50 p-3 text-left ring-1 ring-inset ring-emerald-600/20">
        <Outcome lead={lead} outcome={outcome.data} timeZone={timeZone} onClose={onClose} />
      </div>
    );
  }

  return (
    <div className="w-[24rem] max-w-full rounded-lg bg-emerald-50 p-3 text-left ring-1 ring-inset ring-emerald-600/20">
      <p className="mb-2 flex items-center justify-between text-xs font-semibold text-emerald-900">
        Win {lead.display_name || "this lead"}
        <button
          onClick={onClose}
          aria-label="Cancel"
          className="rounded p-0.5 text-emerald-700 hover:bg-emerald-100"
        >
          <X size={13} />
        </button>
      </p>

      <label className="block text-xs font-medium text-emerald-900">When are they coming in?</label>
      <input
        type="datetime-local"
        value={when}
        onChange={(event) => setWhen(event.target.value)}
        aria-label="Tentative time"
        className="mt-1 w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-sm ring-1 ring-inset ring-emerald-600/20 focus:ring-2 focus:ring-emerald-600/50"
      />
      <p className="mt-1 text-[11px] text-emerald-800">
        {timeZone ? `In ${timeZone}.` : "In your browser's zone."} Leave blank if undecided.
      </p>

      <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-emerald-900">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(event) => setAgreed(event.target.checked)}
          className="size-3.5 rounded border-emerald-600/40"
        />
        They have agreed this time
      </label>

      {error ? (
        <p role="alert" className="mt-2 text-xs text-rose-700">
          {error.message}
        </p>
      ) : null}

      <div className="mt-3 flex items-center gap-1.5">
        <Button
          variant="primary"
          disabled={win.isPending}
          onClick={() => win.mutate()}
          className={cx("bg-emerald-700 px-2.5 py-1.5 text-xs hover:bg-emerald-800")}
        >
          <Check size={13} />
          {win.isPending ? "Saving…" : "Convert to customer"}
        </Button>
        <Button onClick={onClose} disabled={win.isPending} className="px-2.5 py-1.5 text-xs">
          Cancel
        </Button>
      </div>

      <p className="mt-2 text-[11px] leading-relaxed text-emerald-800">
        Creates the contact, marks this won, and holds a booking at the agreed
        time if the slot is free. Twice on the same lead returns the same contact.
      </p>
    </div>
  );
}

function Outcome({
  lead,
  outcome,
  timeZone,
  onClose,
}: {
  lead: Lead;
  outcome: LeadBooking | null | undefined;
  timeZone: string | undefined;
  onClose: () => void;
}) {
  const booking = outcome?.booking;
  return (
    <>
      <p className="mb-1 flex items-center justify-between text-xs font-semibold text-emerald-900">
        Won — {lead.display_name || "this lead"}
        <button
          onClick={onClose}
          aria-label="Done"
          className="rounded px-1.5 py-0.5 text-emerald-700 hover:bg-emerald-100"
        >
          Done
        </button>
      </p>

      {outcome === undefined ? (
        <p className="mt-1 text-xs text-emerald-800">Reporting the booking…</p>
      ) : booking ? (
        <div className="mt-1 rounded-md bg-white p-2 ring-1 ring-inset ring-emerald-600/20">
          <p className="text-xs font-semibold text-emerald-900">
            {formatDateTime(booking.starts_at, timeZone)} · {booking.service_name}
          </p>
          <p className="mt-0.5 text-[11px] text-emerald-700">
            With {booking.employee_name}. Slot held — you will not be double-booked.
          </p>
          <Link to="/scheduling/calendar" className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-800 underline">
            <CalendarDays size={11} /> See it on the calendar
          </Link>
        </div>
      ) : (
        <div className="mt-1 rounded-md bg-white p-2 ring-1 ring-inset ring-amber-600/25">
          <p className="text-xs font-medium text-amber-900">The agreed time was not free</p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-amber-800">
            {outcome?.blocked_reason ?? "The slot is taken."} The win stands — pick another time.
          </p>
          <Link
            to={`/scheduling/calendar`}
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-900 underline"
          >
            <CalendarDays size={11} /> Reserve a slot on the calendar
          </Link>
        </div>
      )}
    </>
  );
}